// TransferHistoryModal.js
// Transfer geçmişini görüntüleme ve geri alma bileşeni

import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Typography,
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Backdrop,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import UndoIcon from '@mui/icons-material/Undo';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  doc,
  getDoc,
  updateDoc,
  setDoc,
  writeBatch,
  serverTimestamp,
  deleteField,
  runTransaction,
} from 'firebase/firestore';

// ==================== STYLED COMPONENTS ====================

const ModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 700,
  maxHeight: '85vh',
  backgroundColor: theme.palette.background.paper,
  borderRadius: 12,
  boxShadow: theme.shadows[24],
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.grey[50],
}));

const Content = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: theme.spacing(2),
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6),
  color: theme.palette.text.secondary,
}));

const BackupItem = styled(ListItem)(({ theme }) => ({
  borderRadius: 8,
  marginBottom: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

// ==================== HELPER FUNCTIONS ====================

/**
 * Transfer yedeklerini getir
 */
const fetchTransferBackups = async (userEmail) => {
  const db = getFirestore();
  const backupsRef = collection(db, `users/${userEmail}/transferBackups`);
  const backupsQuery = query(backupsRef, orderBy('createdAt', 'desc'));
  
  const snapshot = await getDocs(backupsQuery);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt),
    completedAt: doc.data().completedAt?.toDate?.() || null,
    undoneAt: doc.data().undoneAt?.toDate?.() || null,
  }));
};

/**
 * Transfer kilidini al (geri alma işlemi için)
 */
const acquireUndoLock = async (userEmail, backupId) => {
  const db = getFirestore();
  const lockRef = doc(db, `users/${userEmail}/transferLocks/active`);
  
  return await runTransaction(db, async (transaction) => {
    const lockDoc = await transaction.get(lockRef);
    
    if (lockDoc.exists()) {
      const lockData = lockDoc.data();
      const lockTime = lockData.lockedAt?.toDate?.() || new Date(lockData.lockedAt);
      const now = new Date();
      const lockAge = (now - lockTime) / 1000;
      
      if (lockAge < 300) {
        throw new Error(
          `Başka bir işlem devam ediyor. Lütfen ${Math.ceil(300 - lockAge)} saniye bekleyin.`
        );
      }
    }
    
    transaction.set(lockRef, {
      operationType: 'undo',
      backupId,
      lockedAt: serverTimestamp(),
      lockedBy: userEmail,
      status: 'processing'
    });
    
    return true;
  });
};

/**
 * Transfer kilidini serbest bırak
 */
const releaseUndoLock = async (userEmail) => {
  const db = getFirestore();
  const lockRef = doc(db, `users/${userEmail}/transferLocks/active`);
  
  try {
    const lockDoc = await getDoc(lockRef);
    if (lockDoc.exists()) {
      const { deleteDoc } = await import('firebase/firestore');
      await deleteDoc(lockRef);
    }
  } catch (error) {
    console.warn('Kilit silme hatası:', error);
  }
};

/**
 * Transfer işlemini geri al
 */
const undoTransfer = async (backup, userEmail) => {
  const db = getFirestore();
  const batch = writeBatch(db);
  
  console.log('⏪ [TransferUndo] Geri alma başlatılıyor:', backup.id);
  
  // 1. Kaynak müşteriyi yeniden oluştur (eğer silinmişse)
  const sourceCustomerId = backup.sourceCustomerId;
  const sourceCustomerName = backup.sourceCustomerName;
  
  if (sourceCustomerId) {
    const sourceCustomerRef = doc(db, `users/${userEmail}/customerList/${sourceCustomerId}`);
    const sourceCustomerDoc = await getDoc(sourceCustomerRef);
    
    if (!sourceCustomerDoc.exists()) {
      console.log('🔄 [TransferUndo] Kaynak müşteri yeniden oluşturuluyor:', sourceCustomerName);
      
      if (backup.sourceCustomerData) {
        // Orijinal verilerden oluştur
        batch.set(sourceCustomerRef, backup.sourceCustomerData);
      } else {
        // Minimal veri ile oluştur
        const minimalCustomerData = {
          'Müşteri Adı': sourceCustomerName,
          'cariCode': backup.sourceCariCode || '',
          'Onay': 'Onaylandı',
          'createdAt': serverTimestamp(),
          'createdBy': userEmail,
          'restoredFromBackup': true,
          'restoredAt': serverTimestamp(),
          'originalBackupId': backup.id,
        };
        batch.set(sourceCustomerRef, minimalCustomerData);
      }
    }
  }
  
  // 2. Puantajları eski haline getir
  const timeSheetIds = backup.transferredTimeSheetIds || [];
  const sourceCariCode = backup.sourceCariCode || '';
  
  console.log('🔄 [TransferUndo]', timeSheetIds.length, 'puantaj geri yüklenecek');
  
  for (const timeSheetId of timeSheetIds) {
    const timeSheetRef = doc(db, `users/${userEmail}/timeSheets/${timeSheetId}`);
    
    batch.update(timeSheetRef, {
      'Müşteri Adı': sourceCustomerName,
      'Cari Kodu': sourceCariCode,
      // Transfer flaglerini kaldır
      'isTransferred': deleteField(),
      'transferredFrom': deleteField(),
      'transferredTo': deleteField(),
      'transferredAt': deleteField(),
      'transferredBy': deleteField(),
      'transferReason': deleteField(),
      'transferType': deleteField(),
      'originalCustomerName': deleteField(),
      'originalCariCode': deleteField(),
      'originalData': deleteField(),
      // Geri alma bilgisi ekle
      'undoneAt': serverTimestamp(),
      'undoneFromBackup': backup.id,
    });
  }
  
  // 3. Kaynak müşteri debt'i geri yükle
  if (backup.sourceDebtData && sourceCustomerId) {
    const sourceDebtRef = doc(db, `users/${userEmail}/customerList/${sourceCustomerId}/debt/current`);
    
    const restoredDebtData = {
      ...backup.sourceDebtData,
      restoredFromBackup: true,
      restoredAt: serverTimestamp(),
    };
    
    batch.set(sourceDebtRef, restoredDebtData);
    console.log('✅ [TransferUndo] Kaynak müşteri debt\'i geri yüklenecek');
  }
  
  // 4. Hedef müşteri debt'i eski haline getir
  if (backup.targetDebtData && backup.targetCustomerId) {
    const targetDebtRef = doc(db, `users/${userEmail}/customerList/${backup.targetCustomerId}/debt/current`);
    
    const restoredTargetDebtData = {
      ...backup.targetDebtData,
      restoredFromBackup: true,
      restoredAt: serverTimestamp(),
    };
    
    batch.set(targetDebtRef, restoredTargetDebtData);
    console.log('✅ [TransferUndo] Hedef müşteri debt\'i eski haline getirilecek');
  }
  
  // 5. Yedek durumunu güncelle
  const backupRef = doc(db, `users/${userEmail}/transferBackups/${backup.id}`);
  batch.update(backupRef, {
    status: 'undone',
    undoneAt: serverTimestamp(),
    undoneBy: userEmail,
  });
  
  // 6. Commit
  await batch.commit();
  
  console.log('✅ [TransferUndo] Transfer geri alma tamamlandı');
  
  // 7. Activity log
  const activityRef = doc(collection(db, `users/${userEmail}/activityLogs`));
  await setDoc(activityRef, {
    type: 'transfer_undo',
    timestamp: serverTimestamp(),
    backupId: backup.id,
    sourceCustomerName: backup.sourceCustomerName,
    targetCustomerName: backup.targetCustomerName,
    timeSheetCount: timeSheetIds.length,
    userEmail,
    description: `Transfer geri alındı: ${backup.targetCustomerName} → ${backup.sourceCustomerName}`,
  });
};

/**
 * Tarih formatla
 */
const formatDate = (date) => {
  if (!date) return 'Tarih bilinmiyor';
  return new Intl.DateTimeFormat('tr-TR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

/**
 * Status badge'i
 */
const getStatusBadge = (status) => {
  switch (status) {
    case 'completed':
      return <Chip icon={<CheckCircleIcon />} label="Tamamlandı" color="success" size="small" />;
    case 'undone':
      return <Chip icon={<UndoIcon />} label="Geri Alındı" color="warning" size="small" />;
    case 'rolled_back':
      return <Chip icon={<ErrorIcon />} label="Hata (Geri Alındı)" color="error" size="small" />;
    case 'pending':
      return <Chip icon={<HourglassEmptyIcon />} label="İşleniyor" color="info" size="small" />;
    default:
      return <Chip label={status} size="small" />;
  }
};

// ==================== MAIN COMPONENT ====================

const TransferHistoryModal = ({ open, onClose, userEmail }) => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, backup: null });
  
  // Load backups
  const loadBackups = useCallback(async () => {
    if (!userEmail) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await fetchTransferBackups(userEmail);
      setBackups(data);
    } catch (err) {
      console.error('Yedekler yüklenemedi:', err);
      setError('Yedekler yüklenemedi: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [userEmail]);
  
  useEffect(() => {
    if (open && userEmail) {
      loadBackups();
    }
  }, [open, userEmail, loadBackups]);
  
  // Handle undo
  const handleUndoRequest = (backup) => {
    setConfirmDialog({ open: true, backup });
  };
  
  const handleUndoConfirm = async () => {
    const backup = confirmDialog.backup;
    setConfirmDialog({ open: false, backup: null });
    
    if (!backup || !userEmail) return;
    
    setUndoing(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Kilit al
      await acquireUndoLock(userEmail, backup.id);
      
      // Geri alma işlemi
      await undoTransfer(backup, userEmail);
      
      setSuccess(`${backup.targetCustomerName} → ${backup.sourceCustomerName} transferi geri alındı`);
      
      // Listeyi yenile
      await loadBackups();
      
    } catch (err) {
      console.error('Geri alma hatası:', err);
      setError('Geri alma hatası: ' + err.message);
    } finally {
      // Kilidi serbest bırak
      await releaseUndoLock(userEmail);
      setUndoing(false);
    }
  };
  
  const handleUndoCancel = () => {
    setConfirmDialog({ open: false, backup: null });
  };
  
  // Stats
  const totalCount = backups.length;
  const undoableCount = backups.filter(b => b.status === 'completed').length;
  
  return (
    <>
      <Modal open={open} onClose={undoing ? undefined : onClose}>
        <ModalBox>
          <Header>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon color="primary" />
              <Typography variant="h6">Transfer Geçmişi</Typography>
            </Box>
            <IconButton onClick={onClose} disabled={undoing}>
              <CloseIcon />
            </IconButton>
          </Header>
          
          <Content>
            {/* Stats */}
            {backups.length > 0 && (
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Chip label={`Toplam: ${totalCount}`} variant="outlined" />
                <Chip 
                  label={`Geri Alınabilir: ${undoableCount}`} 
                  color={undoableCount > 0 ? 'warning' : 'default'}
                  variant="outlined"
                />
              </Box>
            )}
            
            {/* Alerts */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            )}
            
            {/* Loading */}
            {loading && backups.length === 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}
            
            {/* Empty State */}
            {!loading && backups.length === 0 && (
              <EmptyState>
                <HistoryIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" gutterBottom>
                  Henüz Transfer Yok
                </Typography>
                <Typography color="textSecondary" textAlign="center">
                  Müşteri veri aktarımı yaptığınızda burada görünecek.
                </Typography>
              </EmptyState>
            )}
            
            {/* Backup List */}
            {backups.length > 0 && (
              <List>
                {backups.map((backup) => (
                  <BackupItem key={backup.id}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {backup.sourceCustomerName}
                          </Typography>
                          <Typography color="textSecondary">→</Typography>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {backup.targetCustomerName}
                          </Typography>
                          {getStatusBadge(backup.status)}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="textSecondary">
                            {formatDate(backup.createdAt)}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {(backup.transferredTimeSheetIds?.length || 0)} puantaj aktarıldı
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      {backup.status === 'completed' && (
                        <Button
                          variant="outlined"
                          color="warning"
                          size="small"
                          startIcon={<UndoIcon />}
                          onClick={() => handleUndoRequest(backup)}
                          disabled={undoing}
                        >
                          Geri Al
                        </Button>
                      )}
                    </ListItemSecondaryAction>
                  </BackupItem>
                ))}
              </List>
            )}
          </Content>
        </ModalBox>
      </Modal>
      
      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onClose={handleUndoCancel}>
        <DialogTitle>Transfer Geri Alma Onayı</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.backup && (
              <>
                <strong>'{confirmDialog.backup.targetCustomerName}'</strong> müşterisinden{' '}
                <strong>'{confirmDialog.backup.sourceCustomerName}'</strong> müşterisine yapılan 
                transfer geri alınacak.
                <br /><br />
                <strong>{confirmDialog.backup.transferredTimeSheetIds?.length || 0}</strong> puantaj 
                ve cari bilgileri eski haline getirilecek.
                <br /><br />
                Devam etmek istiyor musunuz?
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUndoCancel}>İptal</Button>
          <Button onClick={handleUndoConfirm} color="warning" variant="contained">
            Geri Al
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Processing Overlay */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
        open={undoing}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress color="inherit" />
          <Typography sx={{ mt: 2 }}>Transfer geri alınıyor...</Typography>
        </Box>
      </Backdrop>
    </>
  );
};

export default TransferHistoryModal;
